import { useEffect, useState } from 'react'
import { FaCalendarDays, FaClock, FaEllipsisVertical, FaEye, FaFilter, FaGavel, FaImage, FaPause, FaPenToSquare, FaPlay, FaPlus, FaStar, FaTrashCan, FaXmark } from 'react-icons/fa6'
import apiClient from '../../../services/apiClient'
import { resolveAssetUrl } from '../../../services/apiClient'
import { DashboardImage } from '../AdminShared'
import { AdminMediaLibrary } from '../media/AdminMediaLibrary'

const ADMIN_AUCTION_PAGE_SIZE = 6

function paginateRows(rows = [], page = 1, pageSize = ADMIN_AUCTION_PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const currentPage = Math.min(Math.max(page, 1), totalPages)
  const start = (currentPage - 1) * pageSize

  return {
    currentPage,
    end: Math.min(start + pageSize, rows.length),
    pageRows: rows.slice(start, start + pageSize),
    start: rows.length ? start + 1 : 0,
    totalPages,
  }
}

function AuctionPagination({ currentPage, end, onPageChange, start, total, totalPages }) {
  if (total <= ADMIN_AUCTION_PAGE_SIZE) return null

  return (
    <div className="obc-admin-pagination" aria-label="Listing pagination">
      <span>Showing {start}-{end} of {total}</span>
      <div>
        <button type="button" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1}>Previous</button>
        <strong>Page {currentPage} of {totalPages}</strong>
        <button type="button" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages}>Next</button>
      </div>
    </div>
  )
}

export function AdminBidAudit({ rows = [] }) {
  const [page, setPage] = useState(1)
  const pagination = paginateRows(rows, page)
  const extendedRows = rows.filter((row) => row.extended === 'Anti-snipe extended')
  const reserveOpenRows = rows.filter((row) => row.reserve === 'Open')

  return (
    <section className="tactical-card obc-admin__table-card obc-admin__bid-audit obc-bid-ledger">
      <div className="obc-admin__panel-head">
        <div>
          <h2>Bid Audit Ledger</h2>
          <span>Accepted bid trail with admin-visible bidder identity and public masked label.</span>
        </div>
      </div>

      <div className="obc-bid-ledger__summary" aria-label="Bid audit summary">
        <article><strong>{rows.length}</strong><span>Total bids</span></article>
        <article><strong>{extendedRows.length}</strong><span>Timer extensions</span></article>
        <article><strong>{reserveOpenRows.length}</strong><span>Reserve-open bids</span></article>
      </div>

      <div className="obc-admin__audit-grid obc-bid-ledger__list">
        {pagination.pageRows.map((row) => (
          <article key={row.id}>
            <div className="obc-bid-ledger__lot">
              <span className={`obc-admin-rule-pill is-${row.status}`}>{row.status}</span>
              <strong>{row.auction}</strong>
              <small>{row.id} / {row.type}</small>
            </div>
            <div>
              <small>Accepted bid</small>
              <strong>{row.amount}</strong>
              <span>Next bid {row.next}</span>
            </div>
            <div>
              <small>Bidder</small>
              <strong>{row.bidder}</strong>
              <span>Public label {row.masked}</span>
            </div>
            <div>
              <small>Outcome</small>
              <strong>{row.reserve}</strong>
              <span>{row.extended}</span>
            </div>
            <time>{row.time}</time>
          </article>
        ))}
      </div>
      <AuctionPagination
        currentPage={pagination.currentPage}
        end={pagination.end}
        onPageChange={setPage}
        start={pagination.start}
        total={rows.length}
        totalPages={pagination.totalPages}
      />
    </section>
  )
}

function buildAuctionResultRows(auctionRows = [], orderRows = []) {
  const orderByAuctionId = new Map(
    orderRows
      .filter((order) => order.rawAuctionId)
      .map((order) => [order.rawAuctionId, order])
  )

  return auctionRows
    .filter((auction) => ['closed', 'won'].includes(auction.rawStatus))
    .map((auction) => {
      const order = orderByAuctionId.get(auction.rawId)
      const hasWinner = Boolean(auction.rawWinnerId || auction.rawHighBidderId)
      const hasOrder = Boolean(order)
      const winnerLabel = hasWinner ? `Winner ${String(auction.rawWinnerId || auction.rawHighBidderId).replace(/^user_/, '')}` : 'No winner'
      const orderLabel = hasOrder
        ? `${order.id} / ${order.workflowStage || order.status}`
        : 'No auction-win order created'

      let reason = 'Auction closed without a winning order.'

      if (auction.rawStatus === 'won' && hasOrder) {
        reason = 'Winner order created after reserve was met.'
      } else if (auction.rawStatus === 'won' && !hasOrder && !hasWinner) {
        reason = 'No winning bidder was recorded.'
      } else if (auction.rawStatus === 'won' && !hasOrder && !auction.rawReserveMet) {
        reason = 'Reserve was not met, so no order was generated.'
      } else if (auction.rawStatus === 'won' && !hasOrder) {
        reason = 'Winning bidder exists, but the winner order is missing.'
      } else if (auction.rawStatus === 'closed' && !hasWinner) {
        reason = 'Auction closed unsold because no winning bidder was recorded.'
      } else if (auction.rawStatus === 'closed' && hasWinner && !auction.rawReserveMet) {
        reason = 'Auction ended with a bidder, but the reserve was not met.'
      } else if (auction.rawStatus === 'closed' && hasOrder) {
        reason = 'Auction closed and an order exists, which usually means it was manually resolved.'
      }

      return {
        id: auction.rawId,
        lot: auction.lot,
        status: auction.rawStatus,
        type: auction.rawType,
        winnerLabel,
        finalBid: auction.currentBid,
        reserveState: auction.reserveState,
        orderLabel,
        orderStatus: hasOrder ? order.status : 'none',
        paymentStatus: auction.rawPaymentStatus || (hasOrder ? order.workflowStage : ''),
        reason,
      }
    })
}

export function AuctionRulesPanel({ rows = [] }) {
  const liveRows = rows.filter((row) => row.status === 'live')
  const antiSnipeRows = rows.filter((row) => row.type === 'anti-snipe')
  const hardCloseRows = rows.filter((row) => row.type === 'hard-close')
  const liveStyleRows = rows.filter((row) => row.type === 'live-style')
  const reserveOpenRows = rows.filter((row) => row.reserveState === 'Open')
  const featuredRows = rows.filter((row) => row.rawFeatured)
  const watchedRows = rows.filter((row) => row.rawStatus === 'scheduled')
  const ruleCards = [
    { label: 'Anti-snipe', value: antiSnipeRows.length, detail: 'Extends near close when configured', tone: 'success' },
    { label: 'Hard close', value: hardCloseRows.length, detail: 'Ends exactly at scheduled close', tone: 'neutral' },
    { label: 'Live style', value: liveStyleRows.length, detail: 'Manual bidding, no proxy flow', tone: 'neutral' },
  ]
  const visibleRuleRows = rows.slice(0, 6)

  return (
    <section className="tactical-card obc-admin__builder obc-admin__rules obc-admin-rules">
      <div className="obc-admin__panel-head">
        <div>
          <h2>Auction Rules Visibility</h2>
          <span>Operational view of bidding rules, reserve state, increments, and close behavior.</span>
        </div>
      </div>

      <div className="obc-admin__rules-summary obc-admin-rules__metrics">
        <article><strong>{liveRows.length}</strong><span>Live lots</span></article>
        <article><strong>{watchedRows.length}</strong><span>Scheduled lots</span></article>
        <article><strong>{reserveOpenRows.length}</strong><span>Reserve open</span></article>
        <article><strong>{featuredRows.length}</strong><span>Featured lots</span></article>
      </div>

      <div className="obc-admin-rules__body">
        <div className="obc-admin-rules__format-grid">
          {ruleCards.map((card) => (
            <article className={`is-${card.tone}`} key={card.label}>
              <div>
                <strong>{card.value}</strong>
                <span>{card.label}</span>
              </div>
              <p>{card.detail}</p>
            </article>
          ))}
        </div>

        <div className="obc-admin-rules__lot-list">
          <div className="obc-admin-rules__subhead">
            <strong>Rule coverage</strong>
            <span>{rows.length ? `Showing ${Math.min(rows.length, 6)} of ${rows.length} lots` : 'No lots available'}</span>
          </div>
          {visibleRuleRows.map((row) => (
            <article key={row.rawId || row.lot}>
              <div>
                <strong>{row.lot}</strong>
                <span>{row.type} / {row.reserveState}</span>
              </div>
              <div>
                <span className={`obc-admin-rule-pill is-${row.rawStatus}`}>{row.status}</span>
                <small>{row.increment} increment</small>
              </div>
            </article>
          ))}
          {!visibleRuleRows.length ? <p>No auction rules to review yet.</p> : null}
        </div>
      </div>
    </section>
  )
}

export function AuctionResultsPanel({ auctionRows = [], orderRows = [] }) {
  const resultRows = buildAuctionResultRows(auctionRows, orderRows)
  const wonRows = resultRows.filter((row) => row.status === 'won')
  const closedRows = resultRows.filter((row) => row.status === 'closed')
  const withOrders = resultRows.filter((row) => row.orderStatus !== 'none')
  const unsoldRows = Math.max(0, closedRows.length - withOrders.length)

  return (
    <section className="tactical-card obc-admin__table-card obc-auction-results">
      <div className="obc-admin__panel-head">
        <div>
          <h2>Auction Results</h2>
          <span>Closed lots, final bidder, reserve state, and whether a winner order was created.</span>
        </div>
      </div>

      <div className="obc-auction-results__summary">
        <article><strong>{resultRows.length}</strong><span>Closed lots</span></article>
        <article><strong>{wonRows.length}</strong><span>Won lots</span></article>
        <article><strong>{withOrders.length}</strong><span>Winner orders</span></article>
        <article><strong>{unsoldRows}</strong><span>Unsold lots</span></article>
      </div>

      <div className="obc-auction-results__grid">
        {resultRows.map((row) => (
          <article key={row.id} className={`obc-auction-results__card is-${row.status}`}>
            <div className="obc-auction-results__card-head">
              <div>
                <span className={`obc-admin-rule-pill is-${row.status}`}>{row.status}</span>
                <strong>{row.lot}</strong>
                <small>{row.id} / {row.type}</small>
              </div>
              <span className="obc-auction-results__final-bid">{row.finalBid}</span>
            </div>

            <div className="obc-auction-results__meta">
              <span>
                <small>Winner</small>
                <strong>{row.winnerLabel}</strong>
              </span>
              <span>
                <small>Reserve</small>
                <strong>{row.reserveState}</strong>
              </span>
              <span>
                <small>Order</small>
                <strong>{row.orderLabel}</strong>
              </span>
              <span>
                <small>Status</small>
                <strong>{row.paymentStatus || 'No payout yet'}</strong>
              </span>
            </div>

            <p>{row.reason}</p>
          </article>
        ))}

        {!resultRows.length ? (
          <div className="obc-auction-results__empty">
            No closed auction results yet. Closed or won lots will appear here once lifecycle completes.
          </div>
        ) : null}
      </div>
    </section>
  )
}

const auctionCategories = ['Cards', 'Memorabilia', 'TCG', 'Sealed Wax', 'Autographs', 'Graded Cards']

function toDatetimeLocalValue(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return localDate.toISOString().slice(0, 16)
}

function createDefaultAuctionSchedule() {
  const startsAt = new Date()
  startsAt.setMinutes(startsAt.getMinutes() + 15)
  const endsAt = new Date(startsAt)
  endsAt.setDate(endsAt.getDate() + 3)

  return {
    startsAt: toDatetimeLocalValue(startsAt),
    endsAt: toDatetimeLocalValue(endsAt),
    featuredStartsAt: '',
    featuredEndsAt: '',
  }
}

function datetimeLocalToIso(value) {
  if (!value) return null

  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? date.toISOString() : null
}

function datetimeValueFromRow(value) {
  if (!value) return ''

  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? toDatetimeLocalValue(date) : ''
}

function auctionEditState(row) {
  return {
    title: row.rawTitle || row.lot || '',
    subtitle: row.rawSubtitle || '',
    description: row.rawDescription || '',
    category: row.rawCategory || 'Cards',
    type: row.rawType || row.type || 'anti-snipe',
    currentBid: String(row.rawCurrentBid ?? 0),
    reservePrice: String(row.rawReservePrice ?? 0),
    minimumIncrement: String(row.rawMinimumIncrement || 25),
    startsAt: datetimeValueFromRow(row.rawStartsAt),
    endsAt: datetimeValueFromRow(row.rawEndsAt),
    featured: row.rawFeatured ? 'true' : 'false',
    featuredStartsAt: datetimeValueFromRow(row.rawFeaturedStartsAt),
    featuredEndsAt: datetimeValueFromRow(row.rawFeaturedEndsAt),
    image: row.rawImages?.[0] || null,
  }
}

export function AdminAuctionBuilder({ apiToken, onRefresh } = {}) {
  const [selectedImage, setSelectedImage] = useState(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isMediaOpen, setIsMediaOpen] = useState(false)
  const [uploadStatus, setUploadStatus] = useState('')
  const [schedule, setSchedule] = useState(createDefaultAuctionSchedule)
  const [activeStep, setActiveStep] = useState(1)

  const updateSchedule = (field, value) => {
    setSchedule((current) => ({ ...current, [field]: value }))
  }

  const closeCreateModal = () => {
    setIsCreateOpen(false)
    setUploadStatus('')
    setActiveStep(1)
  }

  const openCreateModal = () => {
    setUploadStatus('')
    setActiveStep(1)
    setIsCreateOpen(true)
  }

  const goToStep = (step) => {
    setUploadStatus('')
    setActiveStep(step)
  }

  const handleNextStep = (event) => {
    event.preventDefault()
    const form = event.currentTarget.form

    if (activeStep === 1 && !selectedImage) {
      setUploadStatus('Select lot media before continuing.')
      return
    }

    if (activeStep === 2 && form) {
      const title = String(new FormData(form).get('title') || '').trim()
      const subtitle = String(new FormData(form).get('subtitle') || '').trim()

      if (!title || !subtitle) {
        setUploadStatus('Lot title and subtitle are required.')
        return
      }
    }

    setUploadStatus('')
    setActiveStep((current) => Math.min(current + 1, 3))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!apiToken) return

    const formElement = event.currentTarget
    const form = new FormData(formElement)
    const title = String(form.get('title') || '').trim()
    const subtitle = String(form.get('subtitle') || '').trim()
    const description = String(form.get('description') || '').trim()
    const startsAt = datetimeLocalToIso(schedule.startsAt)
    const endsAt = datetimeLocalToIso(schedule.endsAt)
    const featured = form.get('featured') === 'true'
    const featuredStartsAt = featured ? datetimeLocalToIso(schedule.featuredStartsAt) : null
    const featuredEndsAt = featured ? datetimeLocalToIso(schedule.featuredEndsAt) : null
    const currentBid = Number(form.get('currentBid') || 0)
    const reservePrice = Number(form.get('reservePrice') || 0)
    const minimumIncrement = Number(form.get('minimumIncrement') || 25)

    if (!selectedImage) {
      setUploadStatus('Select lot media before creating the auction.')
      setActiveStep(1)
      return
    }

    if (!title || !subtitle) {
      setUploadStatus('Lot title and subtitle are required.')
      setActiveStep(2)
      return
    }

    if (!startsAt || !endsAt || new Date(endsAt) <= new Date(startsAt)) {
      setUploadStatus('Auction close time must be after the start time.')
      setActiveStep(3)
      return
    }

    if (![currentBid, reservePrice, minimumIncrement].every(Number.isFinite) || currentBid < 0 || reservePrice < 0 || minimumIncrement <= 0) {
      setUploadStatus('Opening bid, reserve, and increment must be valid positive numbers.')
      setActiveStep(3)
      return
    }

    if (featured && featuredStartsAt && featuredEndsAt && new Date(featuredEndsAt) <= new Date(featuredStartsAt)) {
      setUploadStatus('Featured end time must be after featured start time.')
      setActiveStep(3)
      return
    }

    try {
      setUploadStatus('Saving auction...')

      await apiClient.createAuction(apiToken, {
        title,
        subtitle,
        description,
        category: form.get('category') || 'Cards',
        type: form.get('type'),
        currentBid,
        reservePrice,
        minimumIncrement,
        startsAt,
        endsAt,
        featured,
        featuredStartsAt,
        featuredEndsAt,
        images: selectedImage ? [selectedImage] : [],
      })

      formElement.reset()
      setSelectedImage(null)
      setSchedule(createDefaultAuctionSchedule())
      setUploadStatus('')
      setActiveStep(1)
      setIsCreateOpen(false)
      await onRefresh?.('Auction created')
    } catch (error) {
      setUploadStatus(error.message || 'Auction save failed')
    }
  }

  return (
    <>
      <section className="tactical-card obc-admin__builder obc-auction-create-launcher">
        <div>
          <span><FaGavel /> Auction setup</span>
          <h2>Create Auction</h2>
          <p>Open a structured form for lot details, bid rules, schedule, feature timing, and reusable media.</p>
        </div>
        <button type="button" onClick={openCreateModal}>
          <FaPlus /> New auction
        </button>
      </section>

      {isCreateOpen ? (
        <div className="obc-auction-create-modal" role="dialog" aria-modal="true" aria-label="Create auction">
          <div className="obc-auction-create-modal__backdrop" onClick={closeCreateModal} />
          <div className="obc-auction-create-modal__panel">
            <header className="obc-auction-create-modal__header">
              <div>
                <span><FaGavel /> Auction operations</span>
                <h2>Create Auction</h2>
              </div>
              <button type="button" onClick={closeCreateModal} aria-label="Close create auction">
                <FaXmark />
              </button>
            </header>

            <form className="obc-admin__builder obc-auction-create" onSubmit={handleSubmit} noValidate>
              <div className="obc-auction-create__steps" aria-label="Create auction steps">
                {[
                  [1, 'Media select', 'Choose the lot image'],
                  [2, 'Lot details', 'Title and listing copy'],
                  [3, 'Bid rules', 'Pricing and schedule'],
                ].map(([step, label, detail]) => (
                  <button
                    type="button"
                    className={activeStep === step ? 'is-active' : activeStep > step ? 'is-complete' : ''}
                    key={step}
                    onClick={() => goToStep(step)}
                  >
                    <strong>{step}</strong>
                    <span>{label}</span>
                    <small>{detail}</small>
                  </button>
                ))}
              </div>

              <div className="obc-auction-create__layout">
                <section className={`obc-auction-create__group obc-auction-create__group--media ${activeStep === 1 ? 'is-active' : ''}`} aria-hidden={activeStep !== 1}>
                  <div className="obc-auction-create__group-head">
                    <strong>Media select</strong>
                    <span>Select or upload the primary auction image before entering lot details.</span>
                  </div>
                  <div className="obc-auction-create__media-step">
                    <button className="obc-admin__file-button" type="button" onClick={() => setIsMediaOpen(true)}>
                      <FaImage /> {selectedImage ? selectedImage.title || selectedImage.originalName || 'Image selected' : 'Choose lot media'}
                    </button>
                    <div className="obc-auction-create__media-note">
                      <strong>{selectedImage ? 'Media selected' : 'No media selected'}</strong>
                      <span>{selectedImage ? selectedImage.caption || selectedImage.filename : 'Use the media library to upload once and reuse images.'}</span>
                    </div>
                  </div>
                </section>

                <section className={`obc-auction-create__group ${activeStep === 2 ? 'is-active' : ''}`} aria-hidden={activeStep !== 2}>
                  <div className="obc-auction-create__group-head">
                    <strong>Lot details</strong>
                    <span>Collector-facing content for list and detail pages.</span>
                  </div>
                  <div className="obc-admin__form-grid">
                    <label>Lot title<input name="title" placeholder="2023 Prizm Auto /10" required /></label>
                    <label>Subtitle<input name="subtitle" placeholder="PSA 10 / low serial / veteran-owned drop" required /></label>
                    <label>Category<select name="category" defaultValue="Cards">{auctionCategories.map((category) => <option value={category} key={category}>{category}</option>)}</select></label>
                    <label>Auction type<select name="type" defaultValue="anti-snipe"><option value="anti-snipe">Anti-sniping</option><option value="hard-close">Hard close</option><option value="live-style">Live style</option></select></label>
                    <label className="is-wide">Description<textarea name="description" placeholder="Condition notes, grade, serial number, provenance, included items, and any buyer-facing lot details." maxLength="1200" /></label>
                  </div>
                </section>

                <section className={`obc-auction-create__group ${activeStep === 3 ? 'is-active' : ''}`} aria-hidden={activeStep !== 3}>
                  <div className="obc-auction-create__group-head">
                    <strong>Bid rules</strong>
                    <span>Pricing guardrails, start time, close time, and homepage feature timing.</span>
                  </div>
                  <div className="obc-admin__form-grid obc-admin__form-grid--compact">
                    <label>Opening bid<input name="currentBid" placeholder="250" type="number" min="0" step="1" defaultValue="0" /></label>
                    <label>Reserve price<input name="reservePrice" placeholder="750" type="number" min="0" step="1" defaultValue="0" /></label>
                    <label>Minimum increment<input name="minimumIncrement" placeholder="50" type="number" min="1" step="1" defaultValue="25" required /></label>
                    <label>Start time<input type="datetime-local" value={schedule.startsAt} onChange={(event) => updateSchedule('startsAt', event.target.value)} required /></label>
                    <label>Close time<input type="datetime-local" value={schedule.endsAt} onChange={(event) => updateSchedule('endsAt', event.target.value)} required /></label>
                    <label>Homepage feature<select name="featured" defaultValue="false"><option value="false">Standard lot</option><option value="true">Feature on homepage</option></select></label>
                    <label>Feature starts<input type="datetime-local" value={schedule.featuredStartsAt} onChange={(event) => updateSchedule('featuredStartsAt', event.target.value)} /></label>
                    <label>Feature ends<input type="datetime-local" value={schedule.featuredEndsAt} onChange={(event) => updateSchedule('featuredEndsAt', event.target.value)} /></label>
                  </div>
                </section>
              </div>

              <div className="obc-auction-create__footer">
                <button type="button" onClick={() => goToStep(Math.max(activeStep - 1, 1))} disabled={activeStep === 1}>Back</button>
                <div className="obc-auction-create__media-note">
                  <strong>Step {activeStep} of 3</strong>
                  <span>{activeStep === 1 ? 'Select reusable media.' : activeStep === 2 ? 'Collect public lot details.' : 'Set bidding rules and timing.'}</span>
                </div>
                {activeStep < 3 ? (
                  <button type="button" onClick={handleNextStep}>Continue</button>
                ) : (
                  <button type="submit"><FaCalendarDays /> Create scheduled auction</button>
                )}
              </div>
              {uploadStatus ? <small className="obc-admin__form-status">{uploadStatus}</small> : null}
            </form>
          </div>
        </div>
      ) : null}

      <AdminMediaLibrary
        apiToken={apiToken}
        open={isMediaOpen}
        onClose={() => setIsMediaOpen(false)}
        onSelect={setSelectedImage}
      />
    </>
  )
}

export function AuctionManager({ apiToken, rows = [], onRefresh }) {
  const [view, setView] = useState('all')
  const [page, setPage] = useState(1)
  const [busyId, setBusyId] = useState('')
  const [localStatus, setLocalStatus] = useState('')
  const [editingRow, setEditingRow] = useState(null)
  const [editFields, setEditFields] = useState(null)
  const [isEditMediaOpen, setIsEditMediaOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [openActionMenuId, setOpenActionMenuId] = useState('')
  const [actionMenuPlacement, setActionMenuPlacement] = useState('bottom')

  const liveRows = rows.filter((row) => row.rawStatus === 'live')
  const scheduledRows = rows.filter((row) => row.rawStatus === 'scheduled')
  const featuredRows = rows.filter((row) => row.rawFeatured)
  const reserveOpenRows = rows.filter((row) => row.reserveState === 'Open')
  const closingSoonRows = rows.filter((row) => {
    if (!row.rawEndsAt || !['live', 'scheduled'].includes(row.rawStatus)) return false

    const closeTime = new Date(row.rawEndsAt).getTime()
    return Number.isFinite(closeTime) && closeTime - Date.now() <= 24 * 60 * 60 * 1000
  })

  const boardViews = [
    { id: 'all', label: 'All lots', count: rows.length },
    { id: 'live', label: 'Live', count: liveRows.length },
    { id: 'scheduled', label: 'Scheduled', count: scheduledRows.length },
    { id: 'featured', label: 'Featured', count: featuredRows.length },
    { id: 'reserve-open', label: 'Reserve open', count: reserveOpenRows.length },
    { id: 'closing-soon', label: 'Closing soon', count: closingSoonRows.length },
    { id: 'closed', label: 'Closed', count: rows.filter((row) => ['closed', 'won'].includes(row.rawStatus)).length },
  ]

  const visibleRows = rows.filter((row) => {
    if (view === 'all') return true
    if (view === 'featured') return row.rawFeatured
    if (view === 'reserve-open') return row.reserveState === 'Open'
    if (view === 'closing-soon') return closingSoonRows.some((item) => item.rawId === row.rawId)
    if (view === 'closed') return ['closed', 'won'].includes(row.rawStatus)
    return row.rawStatus === view
  })
  const pagination = paginateRows(visibleRows, page)

  useEffect(() => {
    if (!openActionMenuId) return undefined

    const closeMenu = (event) => {
      if (event.target?.closest?.('.obc-auction-ops__actions')) return
      setOpenActionMenuId('')
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpenActionMenuId('')
    }

    document.addEventListener('pointerdown', closeMenu)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('pointerdown', closeMenu)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [openActionMenuId])

  const updateAuction = async (row, payload, message) => {
    if (!apiToken || !row.rawId) return

    setBusyId(row.rawId)
    setLocalStatus('')
    try {
      await apiClient.updateAuction(apiToken, row.rawId, payload)
      await onRefresh?.(message)
    } catch (error) {
      const messageText = error.message || 'Auction update failed'
      setLocalStatus(messageText)
      await onRefresh?.(messageText)
    } finally {
      setBusyId('')
    }
  }

  const openEditAuction = (row) => {
    setLocalStatus('')
    setOpenActionMenuId('')
    setEditingRow(row)
    setEditFields(auctionEditState(row))
  }

  const closeEditAuction = () => {
    setEditingRow(null)
    setEditFields(null)
    setIsEditMediaOpen(false)
  }

  const updateEditField = (field, value) => {
    setEditFields((current) => ({ ...current, [field]: value }))
  }

  const selectEditMedia = (asset) => {
    setEditFields((current) => ({ ...current, image: asset }))
    setIsEditMediaOpen(false)
  }

  const saveEditAuction = async (event) => {
    event.preventDefault()
    if (!apiToken || !editingRow?.rawId || !editFields) return

    const title = editFields.title.trim()
    const subtitle = editFields.subtitle.trim()
    const description = editFields.description.trim()
    const startsAt = datetimeLocalToIso(editFields.startsAt)
    const endsAt = datetimeLocalToIso(editFields.endsAt)
    const featured = editFields.featured === 'true'
    const featuredStartsAt = featured ? datetimeLocalToIso(editFields.featuredStartsAt) : null
    const featuredEndsAt = featured ? datetimeLocalToIso(editFields.featuredEndsAt) : null
    const currentBid = Number(editFields.currentBid || 0)
    const reservePrice = Number(editFields.reservePrice || 0)
    const minimumIncrement = Number(editFields.minimumIncrement || 25)

    if (!title || !subtitle) {
      setLocalStatus('Lot title and subtitle are required before saving.')
      return
    }

    if (!startsAt || !endsAt || new Date(endsAt) <= new Date(startsAt)) {
      setLocalStatus('Auction close time must be after the start time.')
      return
    }

    if (![currentBid, reservePrice, minimumIncrement].every(Number.isFinite) || currentBid < 0 || reservePrice < 0 || minimumIncrement <= 0) {
      setLocalStatus('Opening bid, reserve, and increment must be valid positive numbers.')
      return
    }

    if (featured && featuredStartsAt && featuredEndsAt && new Date(featuredEndsAt) <= new Date(featuredStartsAt)) {
      setLocalStatus('Featured end time must be after featured start time.')
      return
    }

    const payload = {
      title,
      subtitle,
      description,
      category: editFields.category || 'Cards',
      type: editFields.type,
      reservePrice,
      minimumIncrement,
      startsAt,
      endsAt,
      featured,
      featuredStartsAt,
      featuredEndsAt,
      images: editFields.image ? [editFields.image] : [],
    }

    if (!editingRow.rawBidCount) {
      payload.currentBid = currentBid
    }

    setBusyId(editingRow.rawId)
    setLocalStatus('Saving auction changes...')
    try {
      await apiClient.updateAuction(apiToken, editingRow.rawId, payload)
      closeEditAuction()
      await onRefresh?.('Auction updated')
    } catch (error) {
      const messageText = error.message || 'Auction update failed'
      setLocalStatus(messageText)
      await onRefresh?.(messageText)
    } finally {
      setBusyId('')
    }
  }

  const deleteAuction = async () => {
    if (!apiToken || !deleteTarget?.rawId) return

    setBusyId(deleteTarget.rawId)
    setLocalStatus('Deleting auction...')
    try {
      await apiClient.deleteAuction(apiToken, deleteTarget.rawId)
      setDeleteTarget(null)
      await onRefresh?.('Auction deleted')
    } catch (error) {
      const messageText = error.message || 'Auction delete failed'
      setLocalStatus(messageText)
      await onRefresh?.(messageText)
    } finally {
      setBusyId('')
    }
  }

  const startAuction = (row) => {
    setOpenActionMenuId('')
    const now = new Date()
    const currentClose = row.rawEndsAt ? new Date(row.rawEndsAt) : null
    const nextClose = currentClose && currentClose > now
      ? currentClose
      : new Date(now.getTime() + 72 * 60 * 60 * 1000)

    return updateAuction(row, {
      status: 'live',
      startsAt: now.toISOString(),
      endsAt: nextClose.toISOString(),
    }, 'Auction started')
  }

  const featureNow = () => ({
    featured: true,
    featuredStartsAt: new Date().toISOString(),
    featuredEndsAt: null,
  })

  const toggleActionMenu = (event, row) => {
    if (openActionMenuId === row.rawId) {
      setOpenActionMenuId('')
      return
    }

    const triggerRect = event.currentTarget.getBoundingClientRect()
    const estimatedMenuHeight = 320
    const bottomSpace = window.innerHeight - triggerRect.bottom
    const topSpace = triggerRect.top

    setActionMenuPlacement(bottomSpace < estimatedMenuHeight && topSpace > bottomSpace ? 'top' : 'bottom')
    setOpenActionMenuId(row.rawId)
  }

  return (
    <section className="tactical-card obc-admin__builder obc-auction-ops">
      <div className="obc-admin__panel-head">
        <div>
          <h2>Auction Management</h2>
          <span>Operate lots by status, reserve pressure, homepage placement, and close timing.</span>
        </div>
        <a className="obc-auction-ops__public-link" href="/live-auctions"><FaEye /> Public floor</a>
      </div>

      <div className="obc-auction-ops__summary">
        <article>
          <FaGavel />
          <div><strong>{liveRows.length}</strong><span>Live lots</span></div>
        </article>
        <article>
          <FaClock />
          <div><strong>{scheduledRows.length}</strong><span>Scheduled</span></div>
        </article>
        <article>
          <FaStar />
          <div><strong>{featuredRows.length}</strong><span>Featured</span></div>
        </article>
        <article className={reserveOpenRows.length ? 'is-warning' : ''}>
          <FaFilter />
          <div><strong>{reserveOpenRows.length}</strong><span>Reserve open</span></div>
        </article>
      </div>

      <div className="obc-auction-ops__toolbar">
        {boardViews.map((item) => (
          <button
            type="button"
            className={view === item.id ? 'is-active' : ''}
            key={item.id}
            onClick={() => {
              setView(item.id)
              setPage(1)
              setOpenActionMenuId('')
            }}
          >
            <span>{item.label}</span>
            <strong>{item.count}</strong>
          </button>
        ))}
      </div>

      {localStatus ? <small className="obc-admin__form-status">{localStatus}</small> : null}

      <div className="obc-auction-ops__list">
        {pagination.pageRows.map((row) => {
          const isBusy = busyId === row.rawId
          const isClosed = ['closed', 'won'].includes(row.rawStatus)
          const canDelete = !row.rawBidCount && row.rawStatus !== 'won'
          const isActionMenuOpen = openActionMenuId === row.rawId

          return (
            <article className={`obc-auction-ops__row is-${row.rawStatus}`} key={row.rawId || row.lot}>
              <DashboardImage src={row.image} alt={row.lot} />
              <div className="obc-auction-ops__main">
                <div className="obc-auction-ops__title-line">
                  <strong>{row.lot}</strong>
                  <span className={`obc-auction-ops__status is-${row.rawStatus}`}>{row.status}</span>
                </div>
                <div className="obc-auction-ops__meta">
                  <span>{row.type}</span>
                  <span>{row.reserve} reserve</span>
                  <span>{row.featured}</span>
                  <span>Closes {row.close}</span>
                </div>
                <div className="obc-auction-ops__numbers">
                  <span><small>Current</small><b>{row.currentBid}</b></span>
                  <span><small>Next</small><b>{row.nextBid}</b></span>
                  <span><small>Increment</small><b>{row.increment}</b></span>
                  <span><small>Bids</small><b>{row.bids}</b></span>
                  <span className={row.reserveState === 'Open' ? 'is-warning' : ''}><small>Reserve</small><b>{row.reserveState}</b></span>
                </div>
              </div>
              <div className={`obc-auction-ops__actions ${isActionMenuOpen ? `is-open is-placement-${actionMenuPlacement}` : ''}`}>
                <button
                  className="obc-auction-ops__menu-trigger"
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isActionMenuOpen}
                  aria-label={`Open actions for ${row.lot}`}
                  onClick={(event) => toggleActionMenu(event, row)}
                >
                  <FaEllipsisVertical />
                  {/* <span>Options</span> */}
                </button>
                {isActionMenuOpen ? (
                  <div className="obc-auction-ops__menu" role="menu">
                    <a role="menuitem" href={`/auctions/${row.rawId}`} onClick={() => setOpenActionMenuId('')}><FaEye /> Preview</a>
                    <button type="button" role="menuitem" onClick={() => openEditAuction(row)} disabled={isBusy}><FaPenToSquare /> Edit auction</button>
                    <button type="button" role="menuitem" onClick={() => startAuction(row)} disabled={isBusy || isClosed || row.rawStatus === 'live'}><FaPlay /> Start auction</button>
                    <button type="button" role="menuitem" onClick={() => { setOpenActionMenuId(''); updateAuction(row, { status: 'scheduled' }, 'Auction moved to scheduled') }} disabled={isBusy || row.rawStatus === 'scheduled'}><FaPause /> Move to scheduled</button>
                    <button type="button" role="menuitem" onClick={() => { setOpenActionMenuId(''); updateAuction(row, featureNow(), 'Auction featured') }} disabled={isBusy || row.rawFeatured}><FaStar /> Feature on homepage</button>
                    <button type="button" role="menuitem" onClick={() => { setOpenActionMenuId(''); updateAuction(row, { featured: false, featuredStartsAt: null, featuredEndsAt: null }, 'Auction unfeatured') }} disabled={isBusy || !row.rawFeatured}><FaStar /> Remove feature</button>
                    <button type="button" role="menuitem" onClick={() => { setOpenActionMenuId(''); updateAuction(row, { status: 'closed' }, 'Auction closed') }} disabled={isBusy || isClosed}><FaPause /> Close auction</button>
                    <button className="is-danger" type="button" role="menuitem" onClick={() => { setOpenActionMenuId(''); setDeleteTarget(row) }} disabled={isBusy || !canDelete}><FaTrashCan /> Delete auction</button>
                  </div>
                ) : null}
              </div>
            </article>
          )
        })}

        {!visibleRows.length ? (
          <div className="obc-auction-ops__empty">
            <FaGavel />
            <div>
              <strong>No auctions in this view</strong>
              <span>Create or filter another auction lane to continue operations.</span>
            </div>
          </div>
        ) : null}
      </div>
      <AuctionPagination
        currentPage={pagination.currentPage}
        end={pagination.end}
        onPageChange={setPage}
        start={pagination.start}
        total={visibleRows.length}
        totalPages={pagination.totalPages}
      />
      {editingRow && editFields ? (
        <div className="obc-auction-edit-modal" role="dialog" aria-modal="true" aria-label={`Edit auction ${editingRow.lot}`}>
          <div className="obc-auction-edit-modal__backdrop" onClick={closeEditAuction} />
          <form className="obc-auction-edit-modal__panel" onSubmit={saveEditAuction} noValidate>
            <header className="obc-auction-edit-modal__header">
              <div>
                <span><FaPenToSquare /> Auction editor</span>
                <h2>Edit Auction</h2>
                <p>{editingRow.rawId} / {editingRow.status} / {editingRow.bids} bids</p>
              </div>
              <button type="button" onClick={closeEditAuction} aria-label="Close auction editor"><FaXmark /></button>
            </header>

            <div className="obc-auction-edit-modal__body">
              <section className="obc-auction-edit-modal__media">
                <DashboardImage src={editFields.image?.url ? resolveAssetUrl(editFields.image.url) : editingRow.image} alt={editFields.title || editingRow.lot} />
                <button type="button" onClick={() => setIsEditMediaOpen(true)}><FaImage /> Replace Media</button>
                <span>{editFields.image ? editFields.image.title || editFields.image.originalName || editFields.image.filename : 'Current auction media'}</span>
              </section>

              <section className="obc-auction-edit-modal__fields">
                <div className="obc-admin__form-grid">
                  <label>Lot title<input value={editFields.title} onChange={(event) => updateEditField('title', event.target.value)} /></label>
                  <label>Subtitle<input value={editFields.subtitle} onChange={(event) => updateEditField('subtitle', event.target.value)} /></label>
                  <label>Category<select value={editFields.category} onChange={(event) => updateEditField('category', event.target.value)}>{auctionCategories.map((category) => <option value={category} key={category}>{category}</option>)}</select></label>
                  <label>Auction type<select value={editFields.type} onChange={(event) => updateEditField('type', event.target.value)}><option value="anti-snipe">Anti-sniping</option><option value="hard-close">Hard close</option><option value="live-style">Live style</option></select></label>
                  <label className="is-wide">Description<textarea value={editFields.description} onChange={(event) => updateEditField('description', event.target.value)} maxLength="1200" /></label>
                </div>

                <div className="obc-admin__form-grid obc-admin__form-grid--compact">
                  <label>Opening bid<input value={editFields.currentBid} onChange={(event) => updateEditField('currentBid', event.target.value)} type="number" min="0" step="1" disabled={editingRow.rawBidCount > 0} /></label>
                  <label>Reserve price<input value={editFields.reservePrice} onChange={(event) => updateEditField('reservePrice', event.target.value)} type="number" min="0" step="1" /></label>
                  <label>Minimum increment<input value={editFields.minimumIncrement} onChange={(event) => updateEditField('minimumIncrement', event.target.value)} type="number" min="1" step="1" /></label>
                  <label>Start time<input type="datetime-local" value={editFields.startsAt} onChange={(event) => updateEditField('startsAt', event.target.value)} /></label>
                  <label>Close time<input type="datetime-local" value={editFields.endsAt} onChange={(event) => updateEditField('endsAt', event.target.value)} /></label>
                  <label>Homepage feature<select value={editFields.featured} onChange={(event) => updateEditField('featured', event.target.value)}><option value="false">Standard lot</option><option value="true">Feature on homepage</option></select></label>
                  <label>Feature starts<input type="datetime-local" value={editFields.featuredStartsAt} onChange={(event) => updateEditField('featuredStartsAt', event.target.value)} /></label>
                  <label>Feature ends<input type="datetime-local" value={editFields.featuredEndsAt} onChange={(event) => updateEditField('featuredEndsAt', event.target.value)} /></label>
                </div>
              </section>
            </div>

            <footer className="obc-auction-edit-modal__footer">
              <span>{editingRow.rawBidCount > 0 ? 'Opening bid is locked after accepted bids.' : 'No bids yet. Opening bid can still be adjusted.'}</span>
              <div>
                <button type="button" onClick={closeEditAuction}>Cancel</button>
                <button type="submit" disabled={busyId === editingRow.rawId}><FaCalendarDays /> Save Auction</button>
              </div>
            </footer>
          </form>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="obc-auction-delete-modal" role="dialog" aria-modal="true" aria-label={`Delete auction ${deleteTarget.lot}`}>
          <div className="obc-auction-delete-modal__backdrop" onClick={() => setDeleteTarget(null)} />
          <section className="obc-auction-delete-modal__panel">
            <button type="button" onClick={() => setDeleteTarget(null)} aria-label="Cancel delete"><FaXmark /></button>
            <FaTrashCan />
            <h2>Delete Auction?</h2>
            <p>{deleteTarget.lot}</p>
            <span>This is allowed only when the lot has no bids or linked orders. This action removes the auction from visitor and admin listings.</span>
            <div>
              <button type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button type="button" onClick={deleteAuction} disabled={busyId === deleteTarget.rawId}>Delete auction</button>
            </div>
          </section>
        </div>
      ) : null}

      <AdminMediaLibrary
        apiToken={apiToken}
        open={isEditMediaOpen}
        onClose={() => setIsEditMediaOpen(false)}
        onSelect={selectEditMedia}
      />
    </section>
  )
}

function AuctionRuleMatrix({ rows = [] }) {
  const [page, setPage] = useState(1)
  const pagination = paginateRows(rows, page)

  return (
    <section className="tactical-card obc-admin__table-card obc-auction-matrix">
      <div className="obc-admin__panel-head">
        <div>
          <h2>Auction Rule Matrix</h2>
          <span>Compare lot rules, pricing guardrails, reserve state, and close timing.</span>
        </div>
      </div>

      <div className="obc-auction-matrix__table" role="table" aria-label="Auction rule matrix">
        <div className="obc-auction-matrix__head" role="row">
          <span>Lot</span>
          <span>Format</span>
          <span>Status</span>
          <span>Pricing</span>
          <span>Reserve</span>
          <span>Close</span>
        </div>
        {pagination.pageRows.map((row) => (
          <article className="obc-auction-matrix__row" role="row" key={row.rawId || row.lot}>
            <div>
              <strong>{row.lot}</strong>
              <small>{row.rawId}</small>
            </div>
            <span>{row.type}</span>
            <span className={`obc-admin-rule-pill is-${row.rawStatus}`}>{row.status}</span>
            <div>
              <strong>{row.currentBid}</strong>
              <small>Next {row.nextBid} / {row.increment}</small>
            </div>
            <div>
              <strong>{row.reserveState}</strong>
              <small>{row.reserve}</small>
            </div>
            <time>{row.close}</time>
          </article>
        ))}
        {!pagination.pageRows.length ? (
          <div className="obc-auction-matrix__empty">Create auctions to populate the rule matrix.</div>
        ) : null}
      </div>
      <AuctionPagination
        currentPage={pagination.currentPage}
        end={pagination.end}
        onPageChange={setPage}
        start={pagination.start}
        total={rows.length}
        totalPages={pagination.totalPages}
      />
    </section>
  )
}

export function AdminAuctionsModule({ apiToken, auctionRows, bidRows, orderRows = [], onRefresh }) {
  const [activeTab, setActiveTab] = useState('management')
  const tabItems = [
    { id: 'management', label: 'Auction Management', detail: 'Operate auction status and homepage placement', count: auctionRows.length, icon: <FaGavel /> },
    { id: 'rules', label: 'Auction Rules Visibility', detail: 'Review active rule coverage', count: auctionRows.length, icon: <FaEye /> },
    { id: 'matrix', label: 'Auction Rule Matrix', detail: 'Compare lot rules in table format', count: auctionRows.length, icon: <FaFilter /> },
    { id: 'audit', label: 'Bid Audit Ledger', detail: 'Accepted bid trail', count: bidRows?.length || 0, icon: <FaClock /> },
    { id: 'results', label: 'Auction Results', detail: 'Winners, order creation, and close reasons', count: buildAuctionResultRows(auctionRows, orderRows).length, icon: <FaCalendarDays /> },
  ]

  return (
    <div className="obc-admin__section">
      <AdminAuctionBuilder apiToken={apiToken} onRefresh={onRefresh} />

      <section className="obc-admin-auction-tabs" aria-label="Auction admin tabs">
        <div className="obc-admin-auction-tabs__nav" role="tablist" aria-label="Auction operations sections">
          {tabItems.map((item) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === item.id}
              className={activeTab === item.id ? 'is-active' : ''}
              onClick={() => setActiveTab(item.id)}
              key={item.id}
            >
              {item.icon}
              <span>{item.label}</span>
              <small>{item.detail}</small>
              <b>{item.count}</b>
            </button>
          ))}
        </div>

        <div className="obc-admin-auction-tabs__panel" role="tabpanel">
          {activeTab === 'management' ? (
            <AuctionManager apiToken={apiToken} rows={auctionRows} onRefresh={onRefresh} />
          ) : null}

          {activeTab === 'rules' ? <AuctionRulesPanel rows={auctionRows} /> : null}

          {activeTab === 'matrix' ? (
            <AuctionRuleMatrix rows={auctionRows} />
          ) : null}

          {activeTab === 'audit' ? (
            bidRows?.length ? <AdminBidAudit rows={bidRows} /> : (
              <section className="tactical-card obc-admin__table-card obc-admin__bid-audit">
                <div className="obc-admin__panel-head">
                  <div>
                    <h2>Bid Audit Ledger</h2>
                    <span>Accepted bid trail with admin-visible bidder identity and public masked label.</span>
                  </div>
                </div>
                <div className="obc-auction-ops__empty">
                  <FaGavel />
                  <div>
                    <strong>No accepted bids yet</strong>
                    <span>Bid activity will appear here once verified users place auction bids.</span>
                  </div>
                </div>
              </section>
            )
          ) : null}

          {activeTab === 'results' ? <AuctionResultsPanel auctionRows={auctionRows} orderRows={orderRows} /> : null}
        </div>
      </section>
    </div>
  )
}
